import { useEffect, useMemo, useState } from "react";
import api from "../api/client";
import logger from "../lib/logger";
import { ChevronDown, ChevronRight, CalendarDays, Loader2 } from "lucide-react";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * Collapsible weekly schedule for one team member, aggregated across every
 * site. Read-only — editing still happens on the main Schedule tab.
 */
export default function WorkerScheduleCard({ worker, sites }) {
  const [open, setOpen] = useState(false);
  const [shifts, setShifts] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || shifts !== null) return;
    setLoading(true);
    api
      .get(`/schedule/worker/${worker.id}`)
      .then(({ data }) => setShifts(data || []))
      .catch((err) => {
        logger.error("Worker schedule load failed:", err);
        setShifts([]);
      })
      .finally(() => setLoading(false));
  }, [open, shifts, worker.id]);

  const siteMap = useMemo(() => Object.fromEntries(sites.map((s) => [s.id, s])), [sites]);

  // Day -> sorted list of {hour, site}
  const byDay = useMemo(() => {
    const out = Array.from({ length: 7 }, () => []);
    for (const s of shifts || []) {
      out[s.day].push({ hour: s.hour, site: siteMap[s.site_id] });
    }
    out.forEach((arr) => arr.sort((a, b) => a.hour - b.hour));
    return out;
  }, [shifts, siteMap]);

  const totalShifts = shifts?.length ?? 0;
  const totalHours = totalShifts; // 1 cell = 1 hour in this scheduler
  const uniqueSites = new Set((shifts || []).map((s) => s.site_id)).size;

  return (
    <div className="border-t border-gray-200" data-testid={`worker-schedule-${worker.id}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`toggle-schedule-${worker.id}`}
        className="w-full flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#002FA7] hover:bg-[#002FA7]/5"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <CalendarDays className="w-3 h-3" />
        <span>This week's schedule</span>
        {shifts && (
          <span className="ml-auto text-gray-500 font-medium normal-case tracking-normal">
            {totalShifts === 0 ? "Nothing scheduled" : `${totalShifts} hours · ${uniqueSites} site${uniqueSites === 1 ? "" : "s"}`}
          </span>
        )}
      </button>

      {open && (
        <div className="px-4 pb-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-3">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading schedule…
            </div>
          ) : totalShifts === 0 ? (
            <div className="text-sm text-gray-500 italic py-3">
              {worker.name} has no shifts this week. Use the Schedule tab to assign some.
            </div>
          ) : (
            <div className="space-y-2">
              {DAY_LABELS.map((label, idx) => {
                const rows = byDay[idx];
                if (!rows.length) {
                  return (
                    <div key={label} className="flex items-baseline gap-3">
                      <div className="w-10 text-xs font-bold text-gray-400 uppercase tracking-widest tabular-nums">{label}</div>
                      <div className="text-xs text-gray-300 italic">Rest day</div>
                    </div>
                  );
                }
                return (
                  <div key={label} className="flex items-baseline gap-3">
                    <div className="w-10 text-xs font-bold text-gray-700 uppercase tracking-widest tabular-nums">{label}</div>
                    <div className="flex flex-wrap gap-1.5 flex-1">
                      {rows.map((r, i) => (
                        <span
                          key={`${label}-${i}`}
                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border text-xs"
                          style={{
                            background: r.site?.color?.bg || "#F3F4F6",
                            color: r.site?.color?.text || "#111827",
                            borderColor: r.site?.color?.border || "#E5E7EB",
                          }}
                          title={r.site?.name}
                        >
                          <span className="font-bold tabular-nums">{String(r.hour).padStart(2, "0")}:00</span>
                          <span className="opacity-80 max-w-[180px] truncate">{r.site?.name || "Unknown site"}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
              <div className="text-[10px] uppercase tracking-widest text-gray-400 pt-3 border-t border-gray-100">
                Total: {totalHours} hour{totalHours === 1 ? "" : "s"} · {uniqueSites} site{uniqueSites === 1 ? "" : "s"}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
